import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { fmtN, fmtP, heatCell } from "@/components/overview/Momentum";
import { TH } from "@/components/overview/StageCells";
import { fmtL } from "./SvcBits";

const LEFT = "sticky left-0 z-[1] bg-inherit w-[170px] min-w-[170px]";

// Workshop scope: rows = RO categories, columns = revenue classes; ₹ or ₹ per RO of the row's category.
export function SvcMatrix({ matrix, workshopName, period }) {
  const [perRo, setPerRo] = useState(false);
  if (!matrix) return null;
  const cols = matrix.columns || [];
  const val = (r, cls) => {
    const v = r.cells?.[cls];
    if (v == null) return "—";
    if (!perRo) return fmtL(v);
    return r.ros ? fmtN(v / r.ros) : "—";
  };
  return (
    <div className="border bg-card rounded-md" data-testid="sov-matrix">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-3">
        <h3 className="font-heading text-sm font-semibold">C · {matrix.code} {workshopName} — Category × Revenue class <span className="text-muted-foreground font-normal text-xs">· {period?.kind === "week" ? period.week : "month-to-date"} achievement · {perRo ? "₹ per RO of the category" : "₹"}</span></h3>
        <div className="ml-auto flex items-center rounded-md border overflow-hidden text-[11px]" data-testid="sov-matrix-mode">
          <button onClick={() => setPerRo(false)} className={`px-2 py-1 font-semibold ${!perRo ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid="sov-matrix-mode-rs">₹</button>
          <button onClick={() => setPerRo(true)} className={`px-2 py-1 font-semibold ${perRo ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`} data-testid="sov-matrix-mode-perro">₹ / RO</button>
        </div>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-xs min-w-[760px]">
          <thead className="border-b sticky top-0 bg-card z-[2]">
            <tr>
              <th className={`${TH} text-left ${LEFT} bg-card z-[3]`}>Category</th>
              <th className={`${TH} text-right`}>ROs</th>
              {cols.map(([k, l]) => <th key={k} className={`${TH} text-right ${k === "total" ? "border-l-2 border-border/80" : ""}`}>{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((r) => (
              <tr key={r.key} className={`border-b last:border-0 hover:bg-muted/50 ${r.total_row ? "bg-secondary font-bold" : "bg-card"}`} data-testid={`sov-matrix-row-${r.key}`}>
                <td className={`px-2 py-1 font-medium whitespace-nowrap ${LEFT}`}>{r.label}</td>
                <td className="px-2 py-1 text-right num">{fmtN(r.ros)}</td>
                {cols.map(([k]) => <td key={k} className={`px-2 py-1 text-right num ${k === "total" ? "border-l-2 border-border/80 font-semibold" : ""}`} data-testid={`sov-matrix-${r.key}-${k}`}>{val(r, k)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Rank list of all workshops on Total GS % (masked rows for workshop heads: rank only).
export function WorkshopPeers({ rows, me, isWeek }) {
  const peers = [...rows].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
  if (!peers.length) return null;
  return (
    <div className="border bg-card rounded-md" data-testid="sov-peers">
      <div className="px-4 py-2.5 border-b"><h3 className="font-heading text-sm font-semibold">Rank among workshops <span className="text-muted-foreground font-normal text-xs">· {isWeek ? "week" : "month"} Total GS Ach % · {peers.some((p) => p.masked) ? "other workshops show rank only" : "click a workshop to open its dashboard"}</span></h3></div>
      <ol className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-border text-xs">
        {peers.map((p) => (
          <li key={p.code} className={`bg-card px-2.5 py-1.5 flex items-center gap-2 ${p.code === me ? "ring-1 ring-inset ring-foreground/50 bg-amber-50" : ""}`} data-testid={`sov-peer-${p.code}`}>
            <span className="num font-bold w-6 text-right">{p.rank ?? "—"}</span>
            {p.masked ? <span className="inline-flex items-center gap-1 text-muted-foreground truncate"><Lock size={10} /> {p.code} <span className="text-[10px] truncate">{p.name}</span></span>
              : <Link to={`/service/workshop/${p.code}`} className="truncate hover:underline"><span className="font-medium">{p.code}</span> <span className="text-muted-foreground text-[10px]">{p.name}</span></Link>}
            {!p.masked && <span className={`ml-auto num font-semibold rounded px-1 text-[10px] ${heatCell(p.cells?.total_gs?.pct)}`}>{fmtP(p.cells?.total_gs?.pct)}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}
